import { CONFIG, ERROR_MESSAGES } from '../constants/config';

// Security-focused validation utilities
export const sanitizeInput = (input) => {
  if (!input) return input;
  
  // Remove HTML tags and dangerous characters
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
};

export const normalizeUrl = (url) => {
  if (!url || !url.trim()) return '';
  
  let cleanUrl = url.trim();
  
  // Check for dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  if (dangerousProtocols.some(protocol => cleanUrl.toLowerCase().startsWith(protocol))) {
    return null; // Invalid URL
  }
  
  // If no protocol, add https://
  if (!cleanUrl.toLowerCase().startsWith('http://') && !cleanUrl.toLowerCase().startsWith('https://')) {
    cleanUrl = 'https://' + cleanUrl;
  }
  
  return cleanUrl;
};

export const validateUrl = (url) => {
  if (!url || !url.trim()) return true; // Optional field
  
  const normalizedUrl = normalizeUrl(url);
  if (normalizedUrl === null) return false; // Dangerous protocol detected
  if (normalizedUrl === '') return true; // Empty is OK
  
  try {
    const urlObj = new URL(normalizedUrl);
    
    // Must be HTTP or HTTPS
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return false;
    }
    
    // Must have a valid hostname
    if (!urlObj.hostname || urlObj.hostname.length === 0) {
      return false;
    }
    
    // Basic hostname validation (contains at least one dot for domain)
    if (!urlObj.hostname.includes('.')) {
      return false;
    }
    
    return true;
  } catch (_) {
    return false;
  }
};

export const validateAmount = (amount) => {
  const numAmount = parseFloat(amount);
  return !isNaN(numAmount) && numAmount >= CONFIG.MINIMUM_PAYMENT_AMOUNT;
};

export const validateFileType = (file) => {
  return CONFIG.ALLOWED_FILE_TYPES.includes(file.type);
};

export const validateFileSize = (file) => {
  return file.size <= CONFIG.MAX_FILE_SIZE_MB * 1024 * 1024;
};

export const validateSubmissionForm = (formData) => {
  const errors = {};

  // Title validation with sanitization
  const sanitizedTitle = sanitizeInput(formData.title);
  if (!sanitizedTitle || !sanitizedTitle.trim()) {
    errors.title = ERROR_MESSAGES.REQUIRED_TITLE;
  } else if (sanitizedTitle.length > 255) {
    errors.title = 'Title must be less than 255 characters';
  } else if (sanitizedTitle !== formData.title) {
    errors.title = 'Title contains invalid characters';
  }

  // Description validation with sanitization
  const sanitizedDescription = sanitizeInput(formData.description);
  if (!sanitizedDescription || !sanitizedDescription.trim()) {
    errors.description = ERROR_MESSAGES.REQUIRED_DESCRIPTION;
  } else if (sanitizedDescription.length > 5000) {
    errors.description = 'Description must be less than 5000 characters';
  } else if (sanitizedDescription !== formData.description) {
    errors.description = 'Description contains invalid characters';
  }

  // URL validation
  if (formData.url && !validateUrl(formData.url)) {
    errors.url = ERROR_MESSAGES.INVALID_URL;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    sanitizedData: {
      title: sanitizedTitle,
      description: sanitizedDescription,
      url: formData.url
    }
  };
};

// Formatting utilities
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const truncateText = (text, maxLength = CONFIG.DESCRIPTION_TRUNCATE_LENGTH) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const sanitizeAmount = (value) => {
  // If empty string, return empty string instead of 0
  if (value === '' || value === null || value === undefined) {
    return '';
  }
  const num = parseFloat(value);
  return isNaN(num) ? '' : num;
};

// URL utilities
export const getProfilePicSrc = (profilePicUrl) => {
  if (!profilePicUrl) return null;
  
  if (profilePicUrl.startsWith('/api/uploads/')) {
    return `${process.env.REACT_APP_API_URL || ''}${profilePicUrl}`;
  }
  return profilePicUrl;
};

// Rank utilities
export const getRankClass = (rank) => {
  if (rank === CONFIG.LEADERBOARD_RANKS.GOLD) return 'rank-1';
  if (rank === CONFIG.LEADERBOARD_RANKS.SILVER) return 'rank-2';
  if (rank === CONFIG.LEADERBOARD_RANKS.BRONZE) return 'rank-3';
  return '';
}; 